// @ts-check
import axios from 'axios'

const BASE = '/api/tenant-phone-numbers'

/** @typedef {import('./schema').components['schemas']} Schemas */
/** @typedef {Schemas['TenantPhoneNumber']} TenantPhoneNumber */

/**
 * GET /api/tenant-phone-numbers/ — all phone numbers for the current tenant (admin).
 * @returns {Promise<TenantPhoneNumber[]>}
 */
export async function listTenantPhoneNumbers() {
  const { data } = await axios.get(`${BASE}/`)
  return data
}

/**
 * GET /api/tenant-phone-numbers/available/ — active, voice-enabled numbers for the current tenant.
 * @returns {Promise<TenantPhoneNumber[]>}
 */
export async function listAvailableNumbers() {
  const { data } = await axios.get(`${BASE}/available/`)
  return data
}

/**
 * GET /api/tenant-phone-numbers/{id}/phone-sessions/ — paginated phone sessions for a phone number.
 * @param {number} id
 * @param {Record<string, string | number>} [params]
 * @returns {Promise<{count: number, next: string|null, previous: string|null, results: import('./schema').components['schemas']['TelehealthPhoneSession'][]}>}
 */
export async function listPhoneNumberPhoneSessions(id, params) {
  const { data } = await axios.get(`${BASE}/${id}/phone-sessions/`, { params })
  return data
}

/**
 * GET /api/tenant-phone-numbers/{id}/ — single phone number detail.
 * @param {number} id
 * @returns {Promise<TenantPhoneNumber>}
 */
export async function getPhoneNumber(id) {
  const { data } = await axios.get(`${BASE}/${id}/`)
  return data
}

/**
 * POST /api/tenant-phone-numbers/{id}/upload-greeting/ — upload voicemail greeting audio.
 * @param {number} id
 * @param {File} file
 * @returns {Promise<TenantPhoneNumber>}
 */
export async function uploadGreeting(id, file) {
  const form = new FormData()
  form.append('file', file)
  const { data } = await axios.post(`${BASE}/${id}/upload-greeting/`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

/**
 * POST /api/tenant-phone-numbers/{id}/delete-greeting/ — remove voicemail greeting.
 * @param {number} id
 * @returns {Promise<TenantPhoneNumber>}
 */
export async function deleteGreeting(id) {
  const { data } = await axios.post(`${BASE}/${id}/delete-greeting/`)
  return data
}

/**
 * POST /api/tenant-phone-numbers/{id}/assign-user/ — assign or unassign a user.
 * @param {number} id
 * @param {number|null} userId
 * @returns {Promise<TenantPhoneNumber>}
 */
export async function assignUser(id, userId) {
  const { data } = await axios.post(`${BASE}/${id}/assign-user/`, {
    user_id: userId,
  })
  return data
}
